import { useEffect, useRef, useState } from 'react'
import styles from './FilterInput.module.css'

interface Props {
  value: string
  onChange: (value: string) => void
  hiddenCount: number
  shownCount: number
  onClearFilter: () => void
  clientPacketsAutocomplete: string[]
  serverPacketsAutocomplete: string[]
}

export default function FilterInput ({
  value,
  onChange,
  hiddenCount,
  shownCount,
  onClearFilter,
  clientPacketsAutocomplete,
  serverPacketsAutocomplete
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [showAutocomplete, setShowAutocomplete] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const allSuggestions = [
    ...clientPacketsAutocomplete.map(name => ({ name, isClient: true })),
    ...serverPacketsAutocomplete.map(name => ({ name, isClient: false }))
  ].sort((a, b) => a.name.localeCompare(b.name))

  const currentWord = value.split(/,\s*/).pop() || ''
  const filteredSuggestions = allSuggestions.filter(
    ({ name }) => name.toLowerCase().includes(currentWord.toLowerCase().replace(/^\$/, ''))
  )

  useEffect(() => {
    setSelectedIndex(0)
  }, [currentWord])

  const acceptSuggestion = (suggestion: string) => {
    const parts = value.split(/,\s*/)
    parts[parts.length - 1] = suggestion
    onChange(parts.join(', '))
    setShowAutocomplete(false)
    inputRef.current?.focus()
  }

  return (
    <div className={styles.filterBar}>
      <span className={styles.filterIcon}>⌕</span>
      <div className={styles.filterInputWrap}>
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          onFocus={() => setShowAutocomplete(true)}
          onBlur={() => {
            setTimeout(() => setShowAutocomplete(false), 200)
          }}
          onKeyDown={e => {
            if (!showAutocomplete) {
              if (e.key === 'Tab') {
                e.preventDefault()
                setShowAutocomplete(true)
              }
              return
            }

            if (e.key === 'ArrowDown') {
              e.preventDefault()
              setSelectedIndex(i => (i + 1) % filteredSuggestions.length)
            } else if (e.key === 'ArrowUp') {
              e.preventDefault()
              setSelectedIndex(i => (i - 1 + filteredSuggestions.length) % filteredSuggestions.length)
            } else if (e.key === 'Enter' && filteredSuggestions.length > 0) {
              e.preventDefault()
              acceptSuggestion(filteredSuggestions[selectedIndex].name)
            } else if (e.key === 'Escape') {
              e.preventDefault()
              setShowAutocomplete(false)
            }
          }}
          placeholder="Filter packets (e.g. entity, $block_display, !position)"
          className={styles.filterInput}
        />
        {showAutocomplete && filteredSuggestions.length > 0 && (
          <div className={styles.autocomplete}>
            {filteredSuggestions.map(({ name, isClient }, index) => (
              <div
                key={name}
                onClick={() => acceptSuggestion(name)}
                className={`${styles.autocompleteItem} ${index === selectedIndex ? styles.autocompleteItemActive : ''} ${isClient ? styles.autocompleteItemClient : styles.autocompleteItemServer}`}
              >
                {name}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className={styles.filterStats}>
        <span>Showing:</span>
        <span>{shownCount}</span>
        <span>·</span>
        <span>Hidden:</span>
        <span>{hiddenCount}</span>
        {value && (
          <button className={styles.clearBtn} onClick={onClearFilter}>Clear</button>
        )}
      </div>
    </div>
  )
}
